/**
 * The **license-locked affordance** the equip-pick leaves (`<FrameSelector>`,
 * `<WeaponSlotEditor>`, `<SystemEditor>`) render — a thin display layer over the SAME
 * single-source helpers the reducer equip gate uses (`meetsLicenseRequirement`
 * / `licenseRequirement` in `@lancer-vtt/domain`). So the grey-out can never drift from
 * the wall: the reducer still hard-rejects equipping an item above the pilot's earned
 * rank; this just makes the player see *why* before clicking — mirror, never re-enforce.
 *
 * `licenseLock(pilot, item, type)` returns `null` when the pilot meets the requirement
 * (the item equips freely) or a `{ label }` describing the unmet rank when it doesn't —
 * e.g. `"Requires Blackbeard II"`. The label reuses `licenseRequirement` for the id +
 * level and resolves the *display name* off the compendium item, never re-deriving the
 * rule.
 */
import {
  licenseRequirement,
  meetsLicenseRequirement,
  type LicensedItem,
  type PilotData,
} from "@lancer-vtt/domain";

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI"];

/** A frame carries no `license` field — its license display name is its own name (or GMS). */
function licenseDisplayName(item: LicensedItem, type: "frame" | "weapon" | "system" | "mod"): string {
  if (type === "frame") return item.source === "GMS" ? "GMS" : (item.name ?? "license");
  return item.license ?? "license";
}

/**
 * `null` when the pilot may equip the item; otherwise the lock with a requirement label
 * the picker shows on the greyed, disabled option. Mirrors `meetsLicenseRequirement`
 * exactly — same predicate the reducer's equip rules call — so a locked pick the leaf
 * greys is precisely the pick the reducer would hard-reject.
 */
export function licenseLock(
  pilot: PilotData,
  item: LicensedItem,
  type: "frame" | "weapon" | "system" | "mod",
): { label: string } | null {
  if (meetsLicenseRequirement(pilot, item, type)) return null;
  const req = licenseRequirement(item, type);
  // `meetsLicenseRequirement` is false ⇒ there IS a requirement, but guard anyway.
  if (!req) return null;
  const name = licenseDisplayName(item, type);
  return { label: `Requires ${name} ${ROMAN[req.level] ?? req.level}` };
}
